import { ICheckDisbursementFormData } from 'types/order/escrow/checks/ICheckDisbursementFormData';
import { IWireDisbursementFormData } from 'types/order/escrow/wires/IWireDisbursementFormData';
import { ICopiedDisbursementData } from '../context/DisbursementCopyContext';
import {
  IDisbursementFormData,
  isCheckDisbursementFormData,
  isWireDisbursementFormData,
} from 'types/order/escrow/IDisbursementFormData';

export const extractCopyableData = (
  formData: ICheckDisbursementFormData | IWireDisbursementFormData
): ICopiedDisbursementData => {
  if (isCheckDisbursementFormData(formData)) {
    return {
      name: formData.name,
      mailingAddress: {
        streetAddress: formData.mailingAddress?.streetAddress,
        unit: formData.mailingAddress?.unit,
        city: formData.mailingAddress?.city,
        stateAbbreviation: formData.mailingAddress?.stateAbbreviation,
        zipCode: formData.mailingAddress?.zipCode,
      },
    };
  } else if (isWireDisbursementFormData(formData)) {
    return {
      name: formData.beneficiary,
      wireDetails: {
        beneficiary: formData.beneficiary,
        routingNumber: formData.routingNumber,
        accountNumber: formData.accountNumber,
        bankName: formData.bankName,
      },
    };
  }

  throw new Error('Unsupported disbursement type for copying');
};

export const applyCopiedDataToForm = (
  currentFormData: ICheckDisbursementFormData | IWireDisbursementFormData,
  copiedData: ICopiedDisbursementData
): ICheckDisbursementFormData | IWireDisbursementFormData => {
  if (isCheckDisbursementFormData(currentFormData)) {
    return {
      ...currentFormData,
      name: copiedData.name || currentFormData.name,
      mailingAddress: {
        ...currentFormData.mailingAddress,
        streetAddress: copiedData.mailingAddress?.streetAddress || currentFormData.mailingAddress?.streetAddress || '',
        unit:
          copiedData.mailingAddress?.unit !== undefined
            ? copiedData.mailingAddress.unit
            : currentFormData.mailingAddress?.unit,
        city: copiedData.mailingAddress?.city || currentFormData.mailingAddress?.city || '',
        stateAbbreviation:
          copiedData.mailingAddress?.stateAbbreviation || currentFormData.mailingAddress?.stateAbbreviation || '',
        zipCode: copiedData.mailingAddress?.zipCode || currentFormData.mailingAddress?.zipCode || '',
      },
    };
  }

  if (isWireDisbursementFormData(currentFormData)) {
    return {
      ...currentFormData,
      beneficiary: copiedData.wireDetails?.beneficiary || copiedData.name || currentFormData.beneficiary,
      routingNumber: copiedData.wireDetails?.routingNumber || currentFormData.routingNumber,
      accountNumber: copiedData.wireDetails?.accountNumber || currentFormData.accountNumber,
      bankName: copiedData.wireDetails?.bankName || currentFormData.bankName,
    };
  }

  throw new Error('Unsupported disbursement type for applying copied data');
};

export const hasCopyableFields = (data: IDisbursementFormData): boolean => {
  if (isCheckDisbursementFormData(data)) {
    const hasName = Boolean(data.name?.trim());
    const hasAddress = Boolean(
      data.mailingAddress?.streetAddress?.trim() ||
        data.mailingAddress?.unit?.trim() ||
        data.mailingAddress?.city?.trim() ||
        data.mailingAddress?.stateAbbreviation?.trim() ||
        data.mailingAddress?.zipCode?.trim()
    );

    return hasName || hasAddress;
  }

  if (isWireDisbursementFormData(data)) {
    const hasBeneficiary = Boolean(data.beneficiary?.trim());
    const hasWireDetails = Boolean(data.routingNumber?.trim() || data.accountNumber?.trim() || data.bankName?.trim());

    return hasBeneficiary || hasWireDetails;
  }

  return false;
};
